<script setup>
import { requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { computed, ref, watch } from 'vue';
import FormRadio from '../../atoms/form/FormRadio.vue';
import { customFieldProps } from '../../../utils/custom-fields';
import CustomFieldWrapper from './CustomFieldWrapper.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import ComboBox from '../../headless/ComboBox.vue';

const props = defineProps({ ...customFieldProps });

const model = defineModel({
    type: Number,
});

const DISPLAY_AS_DROPDOWN_THRESHOLD = 5;
const showAsDropdown = computed(() => props.options.length > DISPLAY_AS_DROPDOWN_THRESHOLD);
const comboboxModel = ref(props.options.find(({ value }) => value === model.value) ?? null);
watch(comboboxModel, (newComboboxModelValue) => {
    model.value = newComboboxModelValue.value;
});

const rules = {
    model: {
        required: requiredIf(() => props.required),
    },
};

const v$ = useVuelidate(rules, { model }, { $registerAs: `custom-field-${props.id}` });
</script>

<template>
    <CustomFieldWrapper
        :error="v$.$error"
        :label="label"
        :required="required"
        :class="!showAsDropdown && 'flex flex-col gap-xs'"
        data-qa="custom field radio"
    >
        <template v-if="showAsDropdown">
            <ComboBox
                v-model="comboboxModel"
                class="w-[328px]"
                :screen-reader-label="label"
                :options
                :status="v$.$error ? 'error' : undefined"
                :data-qa="`custom field ${id} combobox`"
            />
        </template>
        <template v-else>
            <FormRadio
                v-for="option in options"
                :key="`custom-field-option-${option.value}`"
                :model-value="option.value"
                :checked="option.value === model"
                :name="`custom-field-${id}`"
                data-qa="custom field radio option"
                @update:model-value="model = $event"
            >
                {{ option.label }}
            </FormRadio>
        </template>
        <FormMessage
            v-if="v$.$error"
            type="error"
            data-qa="custom field radio required message"
        >
            <template v-if="v$.$error">
                Bitte wähle eine Option aus.
            </template>
        </FormMessage>
    </CustomFieldWrapper>
</template>
